import { useCallback } from 'react';
import confetti from 'canvas-confetti';

/**
 * useBlessingClick — fires a small premium gold sparkle burst exactly at the
 * cursor / centre of the clicked element. Used to give every CTA a divine-feel
 * confirmation of the user's action, without sound (silent visual blessing).
 *
 * Usage:
 *   const blessing = useBlessingClick();
 *   <button onClick={(e) => { blessing(e); doStuff(); }} />
 */
const useBlessingClick = () => {
  return useCallback((event) => {
    // Find click origin in viewport coords; fallback to centre of target
    let x = 0.5, y = 0.5;
    if (event && (event.clientX || event.touches?.[0])) {
      const cx = event.clientX ?? event.touches?.[0]?.clientX;
      const cy = event.clientY ?? event.touches?.[0]?.clientY;
      x = cx / window.innerWidth;
      y = cy / window.innerHeight;
    } else if (event?.currentTarget?.getBoundingClientRect) {
      const r = event.currentTarget.getBoundingClientRect();
      x = (r.left + r.width / 2) / window.innerWidth;
      y = (r.top + r.height / 2) / window.innerHeight;
    }

    // Outer wider sparkle ring — gold + ivory
    confetti({
      particleCount: 14,
      spread: 70,
      startVelocity: 22,
      gravity: 0.55,
      ticks: 80,
      scalar: 0.85,
      shapes: ['circle'],
      colors: ['#D4AF37', '#F3E5AB', '#FFD700', '#C9A961'],
      origin: { x, y },
      disableForReducedMotion: true,
    });

    // Inner tight star burst — slower, lingering shimmer
    setTimeout(() => {
      confetti({
        particleCount: 18,
        spread: 360,
        startVelocity: 12,
        gravity: 0.25,
        ticks: 100,
        scalar: 0.55,
        shapes: ['star'],
        colors: ['#D4AF37', '#F3E5AB'],
        origin: { x, y },
        disableForReducedMotion: true,
      });
    }, 60);
  }, []);
};

export default useBlessingClick;
